import { useMemo } from 'react';
import { Formik, Form } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { AutomaticNamespaceProvisioningFormFields } from './AutomaticNamespaceProvisioningFormFields';
import { AutomaticNamespaceProvisioningFormValues } from './types';
import { useAutomaticNamespaceProvisioningInitialValues } from './useAutomaticNamespaceProvisioningInitialValues';
import { createAutomaticNamespaceProvisioningValidationSchema } from './automaticNamespaceProvisioningValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: AutomaticNamespaceProvisioningFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

export function AutomaticNamespaceProvisioningPolicyForm({
  onSubmit,
  mode,
  policy,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useAutomaticNamespaceProvisioningInitialValues(policy);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createAutomaticNamespaceProvisioningValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<AutomaticNamespaceProvisioningFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="automatic-namespace-provisioning-k8s" />
            )}
            <AutomaticNamespaceProvisioningFormFields />
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="automatic-namespace-provisioning-k8s"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>

        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="automatic-namespace-provisioning-k8s"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}
